/**
 * Blok Back-up in Instellingen > Meer. L3b van blok L (Frank, 19 oktober 2026).
 *
 * Een eigenaar maakt hier een versleutelde back-up (L3a) en ziet meteen de uitslag van
 * de herstel-test; daaronder de back-ups met datum en vingerafdruk. Voor een lid toont
 * het blok niets: de backend zegt `mag_maken` en weigert een lid hoe dan ook.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * De zin bij een fout van de server, in de taal van het scherm. PUUR op status en detail.
 *
 * Glasronde 0.9.178: de server geeft een vaste code (`detail.code`) en het scherm zegt
 * welke stap je zet, met een verwijzing naar het blok PQC-sleutel. Tot dan las dit de
 * tekst van een foutmelding, en een fout uit een andere stap gaf alleen "mislukt".
 */
const SLEUTELZIN = {
  geen_sleutelbos: "Een back-up kan alleen in de app op de Mac, want de sleutel staat in de sleutelbos van de Mac.",
  geen_sleutel: "Er is nog geen PQC-sleutel. Maak er een in het blok PQC-sleutel hieronder en exporteer hem; daarna kun je een back-up maken.",
  geen_export: "Je PQC-sleutel is nog niet geëxporteerd. Exporteer hem in het blok PQC-sleutel hieronder; zonder export is een back-up niet terug te zetten.",
  export_klopt_niet: "De sleutel-export hoort niet bij je sleutel. Exporteer hem opnieuw in het blok PQC-sleutel hieronder.",
};

export function foutTekst(status, detail, t) {
  if (status === 409) return t("Er loopt al een back-up.");
  if (status === 403) return t("Alleen een eigenaar kan een back-up maken.");
  const code = detail && typeof detail === "object" ? detail.code : null;
  if (SLEUTELZIN[code]) return t(SLEUTELZIN[code]);
  return t("Back-up maken is mislukt.");
}

/** Of de fout naar het blok PQC-sleutel verwijst. PUUR. */
export function naarSleutelblok(detail) {
  return Boolean(detail && typeof detail === "object" && SLEUTELZIN[detail.code] && detail.code !== "geen_sleutelbos");
}

/** Wat er over de herstel-test gezegd wordt: "geslaagd", "mislukt" of "onbekend". PUUR. */
export function herstelStand(waarde) {
  if (waarde === true) return "geslaagd";
  if (waarde === false) return "mislukt";
  return "onbekend";
}

const HERSTEL_TEKST = {
  geslaagd: "Herstel-test geslaagd",
  mislukt: "Herstel-test mislukt",
  onbekend: "Herstel-test onbekend",
};

export default function BackupBlok() {
  const { t, taal } = useTaal();
  const [stand, setStand] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [uitslag, setUitslag] = useState(null);
  const [fout, setFout] = useState("");
  const [verwijs, setVerwijs] = useState(false);

  const laden = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/backup`);
      setStand(r?.data || null);
    } catch {
      setStand(null);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const maak = async () => {
    setBezig(true);
    setFout("");
    setVerwijs(false);
    setUitslag(null);
    try {
      const r = await axios.post(`${API}/settings/backup`);
      setUitslag(r?.data || null);
      await laden();
    } catch (e) {
      setFout(foutTekst(e?.response?.status, e?.response?.data?.detail, t));
      setVerwijs(naarSleutelblok(e?.response?.data?.detail));
    } finally {
      setBezig(false);
    }
  };

  if (!stand?.mag_maken) return null;
  const datum = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(taal === "en" ? "en-GB" : "nl-NL");
  };
  const h = uitslag?.herstel_test;
  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="backup-blok">
      <h4 className="font-medium text-sm">{t("Back-up")}</h4>
      <p className="text-xs text-muted-foreground">
        {t("Een versleutelde back-up van je database, vectoren, werkruimtes en instellingen, met je eigen PQC-sleutel. Daarna opent een herstel-test hem met je sleutel-export in een wegwerpmap; je echte database wordt niet aangeraakt.")}
      </p>
      <Button type="button" variant="outline" size="sm" onClick={maak} disabled={bezig} data-testid="backup-maak"
              className="h-11 md:h-8">
        {bezig ? t("Bezig met de back-up...") : t("Back-up maken")}
      </Button>
      {fout && <p className="text-sm text-destructive" role="alert" data-testid="backup-fout">{fout}</p>}
      {verwijs && (
        <a href="#pqc-blok" className="text-xs text-[hsl(var(--accent))] hover:underline" data-testid="backup-naar-sleutel"
           onClick={(e) => { e.preventDefault(); document.getElementById("pqc-blok")?.scrollIntoView({ behavior: "smooth" }); }}>
          {t("Naar het blok PQC-sleutel")}
        </a>
      )}
      {h && (
        <div className="text-xs space-y-0.5" data-testid="backup-uitslag" data-geslaagd={h.geslaagd ? "ja" : "nee"}>
          <p className="font-medium">{t(HERSTEL_TEKST[herstelStand(h.geslaagd)])}</p>
          <p>{t("Integriteit")}: {h.integrity === "ok" ? t("in orde") : h.integrity}</p>
          <p>{t("Tellingen per tabel")}: {h.tellingen_gelijk ? t("gelijk") : t("niet gelijk")}</p>
          <p>{t("Database")}: {h.database_gelijk ? t("gelijk") : t("niet gelijk")}</p>
          <p>{t("Bestanden")}: {h.bestanden - (h.bestanden_afwijkend || []).length} / {h.bestanden} {t("gelijk")}</p>
        </div>
      )}
      <div className="space-y-1" data-testid="backup-lijst">
        {(stand.backups || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("Nog geen back-ups.")}</p>
        ) : stand.backups.map((b) => (
          <div key={b.naam} className="text-xs border-t pt-1" data-testid={`backup-rij-${b.naam}`}>
            <span className="font-medium">{datum(b.gemaakt)}</span>
            {" · "}<span className="font-mono" title={b.sha256}>{b.sha256.slice(0, 16)}</span>
            {" · "}{t(HERSTEL_TEKST[herstelStand(b.herstel_geslaagd)])}
            {b.klopt === false && <span className="text-destructive"> · {t("bestand gewijzigd sinds het maken")}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
